import { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
} from '@mui/material';
import { ContentCopy as CopyIcon, Clear as ClearIcon } from '@mui/icons-material';
import QRCodeSVG from '../QRCodeSVG';
import AnimatedAvatar from '../AnimatedAvatar';

interface WalletExchangeDemoProps {
  className?: string;
}

export default function WalletExchangeDemo({ className }: WalletExchangeDemoProps) {
  // State for wallet send functionality
  const [showWalletSend, setShowWalletSend] = useState(false);
  const [sendDestinationAddress, setSendDestinationAddress] = useState('');
  
  // State for exchange receive functionality
  const [showExchangeReceive, setShowExchangeReceive] = useState(false);
  const depositAddress = 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wle';

  const walletRef = useRef<HTMLDivElement>(null);
  const exchangeRef = useRef<HTMLDivElement>(null);

  // Wallet send handlers
  const handleSendClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setShowWalletSend(true);
  }, []);

  const handleCloseSend = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setShowWalletSend(false);
    setSendDestinationAddress('');
  }, []);

  const handleAddressChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSendDestinationAddress(event.target.value);
  }, []);

  // Exchange receive handlers
  const handleReceiveClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setShowExchangeReceive(true);
  }, []);

  const handleCloseReceive = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setShowExchangeReceive(false);
  }, []);

  return (
    <Box className={className}>
      <Typography
        variant="h3"
        component="h2"
        sx={{
          textAlign: 'center',
          mb: 3,
          fontWeight: 'bold',
          color: '#333',
          fontSize: { xs: '2rem', md: '2.5rem' },
        }}
      >
        See Hashatar in action
      </Typography>

      <Typography
        variant="h6"
        component="p"
        sx={{
          textAlign: 'center',
          mb: 6,
          color: '#666',
          maxWidth: '600px',
          mx: 'auto',
          lineHeight: 1.6,
        }}
      >
        Experience how hashatar provides visual verification when transferring cryptocurrency addresses between wallet and exchange platforms.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          gap: { xs: '20px', sm: '40px', md: '60px' },
          flexWrap: 'wrap',
          px: { xs: 1, sm: 2 },
        }}
      >
        {/* Bitcoin Wallet Stack */}
        <Stack spacing={2} alignItems="center">
          {/* Bitcoin Wallet Mockup */}
          <Box
            sx={{
              width: { xs: '280px', sm: '300px', md: '320px' },
              height: { xs: '520px', sm: '560px', md: '600px' },
              backgroundColor: '#1a1a1a',
              borderRadius: '12px',
              border: '2px solid #333',
              padding: { xs: '12px', md: '16px' },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '8px', md: '12px' },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              maxWidth: 'calc(100vw - 40px)', // Ensure it never exceeds viewport
              position: 'relative', // For consistent height
            }}
          >
          {!showWalletSend ? (
            <>
              {/* Wallet Header */}
              <Box sx={{ borderBottom: '1px solid #333', paddingBottom: '12px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f7931a', // Bitcoin orange
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  Bitcoin Wallet
                </Typography>
              </Box>
              
              {/* Balance Section */}
              <Box sx={{ textAlign: 'center', marginTop: '20px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '8px' }}
                >
                  Total Balance
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    fontFamily: 'Monaco, monospace',
                  }}
                >
                  0.12589 BTC
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginTop: '4px' }}
                >
                  ≈ $5,243.71 USD
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '24px',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: '#f7931a',
                    color: '#000',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    animation: 'pulse-glow-orange 2s ease-in-out infinite',
                    '@keyframes pulse-glow-orange': {
                      '0%, 100%': {
                        boxShadow: '0 0 5px rgba(247, 147, 26, 0.4), 0 0 10px rgba(247, 147, 26, 0.3), 0 0 15px rgba(247, 147, 26, 0.2)',
                        transform: 'scale(1)',
                      },
                      '50%': {
                        boxShadow: '0 0 10px rgba(247, 147, 26, 0.6), 0 0 20px rgba(247, 147, 26, 0.4), 0 0 30px rgba(247, 147, 26, 0.3)',
                        transform: 'scale(1.02)',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#e8851c',
                      transform: 'translateY(-1px) scale(1.02)',
                      boxShadow: '0 4px 12px rgba(247, 147, 26, 0.3), 0 0 15px rgba(247, 147, 26, 0.5)',
                      animation: 'none',
                    },
                  }}
                  onClick={handleSendClick}
                >
                  Send
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: 'transparent',
                    color: '#f7931a',
                    border: '2px solid #f7931a',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                >
                  Receive
                </Box>
              </Box>

              {/* Recent Transactions */}
              <Box sx={{ marginTop: '24px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '12px' }}
                >
                  Recent Activity
                </Typography>
                {[1, 2, 3].map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < 2 ? '1px solid #333' : 'none',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: '#fff', fontSize: '12px' }}
                      >
                        {index === 0 ? 'Received' : 'Sent'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: '#666', fontSize: '10px' }}
                      >
                        2 hours ago
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: index === 0 ? '#4caf50' : '#f44336',
                        fontFamily: 'Monaco, monospace',
                        fontSize: '12px',
                      }}
                    >
                      {index === 0 ? '+' : '-'}0.00{index + 1}23 BTC
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <>
              {/* Send UI Header */}
              <Box sx={{ borderBottom: '1px solid #333', paddingBottom: '12px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#f7931a',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  Send Bitcoin
                </Typography>
              </Box>

              {/* Address Input */}
              <Box sx={{ marginTop: '16px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '6px' }}
                >
                  Destination Address
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="text"
                    autoComplete="new-password"
                    data-1p-ignore
                    data-lpignore="true"
                    value={sendDestinationAddress}
                    onChange={handleAddressChange}
                    placeholder="Enter Bitcoin address..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#2a2a2a',
                        color: '#fff',
                        fontSize: '14px',
                        paddingRight: sendDestinationAddress ? '48px' : '14px', // Space for clear button
                        '& fieldset': {
                          borderColor: '#444',
                        },
                        '&:hover fieldset': {
                          borderColor: '#f7931a',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f7931a',
                        },
                      },
                    }}
                  />
                  {sendDestinationAddress && (
                    <IconButton
                      size="small"
                      onClick={() => setSendDestinationAddress('')}
                      sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#888',
                        '&:hover': {
                          color: '#f7931a',
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Hashatar Preview */}
              <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '8px' }}
                >
                  Hashatar
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: { xs: '120px', md: '140px' },
                    height: { xs: '120px', md: '140px' },
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    margin: '0 auto',
                    backgroundColor: '#000',
                  }}
                  ref={walletRef}
                >
                  <AnimatedAvatar 
                    text={sendDestinationAddress || 'Hashatar'} 
                    size={64}
                    containerRef={walletRef}
                  />
                </Box>
              </Box>

              {/* Amount Section */}
              <Box sx={{ marginTop: '16px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '6px' }}
                >
                  Amount
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="text"
                    autoComplete="off"
                    placeholder="0.00000000"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#2a2a2a',
                        color: '#fff',
                        fontSize: '14px',
                        fontFamily: 'Monaco, monospace',
                        '& fieldset': {
                          borderColor: '#444',
                        },
                        '&:hover fieldset': {
                          borderColor: '#f7931a',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#f7931a',
                        },
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#f7931a',
                      fontWeight: 'bold',
                      minWidth: '35px',
                    }}
                  >
                    BTC
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: 'auto',
                }}
              >
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseSend}
                  sx={{
                    color: '#888',
                    borderColor: '#444',
                    '&:hover': {
                      borderColor: '#666',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!sendDestinationAddress.trim()}
                  sx={{
                    backgroundColor: '#f7931a',
                    color: '#000',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#e8851c',
                    },
                    '&:disabled': {
                      backgroundColor: '#444',
                      color: '#666',
                    },
                  }}
                >
                  Send
                </Button>
              </Box>
            </>
          )}
          </Box>

          {/* Wallet Instruction Label */}
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: '#f7931a',
              fontSize: '14px',
              opacity: showWalletSend ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
              height: '20px', // Maintain consistent height
            }}
          >
            Click "Send" to start a Bitcoin transfer
          </Typography>
        </Stack>

        {/* Crypto Exchange Stack */}
        <Stack spacing={2} alignItems="center">
          {/* Crypto Exchange Mockup */}
          <Box
            sx={{
              width: { xs: '280px', sm: '300px', md: '320px' },
              height: { xs: '520px', sm: '560px', md: '600px' },
              backgroundColor: '#0f1419',
              borderRadius: '12px',
              border: '2px solid #1e2832',
              padding: { xs: '12px', md: '16px' },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '8px', md: '12px' },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              maxWidth: 'calc(100vw - 40px)', // Ensure it never exceeds viewport
              position: 'relative', // For consistent height
            }}
          >
          {!showExchangeReceive ? (
            <>
              {/* Exchange Header */}
              <Box sx={{ borderBottom: '1px solid #1e2832', paddingBottom: '12px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#00d4aa', // Binance-like green
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  Crypto Exchange
                </Typography>
              </Box>

              {/* Market Overview */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '8px' }}
                >
                  Market Overview
                </Typography>
                {[
                  { symbol: 'BTC/USD', price: '$41,847.23', change: '+2.14%', positive: true },
                  { symbol: 'ETH/USD', price: '$2,547.89', change: '-0.87%', positive: false },
                  { symbol: 'ADA/USD', price: '$0.4521', change: '+5.23%', positive: true },
                ].map((coin, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < 2 ? '1px solid #1e2832' : 'none',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}
                    >
                      {coin.symbol}
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#fff',
                          fontSize: '12px',
                          fontFamily: 'Monaco, monospace',
                        }}
                      >
                        {coin.price}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: coin.positive ? '#00d4aa' : '#f6465d',
                          fontSize: '10px',
                          fontFamily: 'Monaco, monospace',
                        }}
                      >
                        {coin.change}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Portfolio Value */}
              <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '6px' }}
                >
                  Portfolio Value
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    fontFamily: 'Monaco, monospace',
                  }}
                >
                  $12,847.32
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#00d4aa', marginTop: '4px' }}
                >
                  +$347.21 (2.78%) 24h
                </Typography>
              </Box>

              {/* Trading Actions */}
              <Box
                sx={{
                  display: 'flex',
                  gap: '12px',
                  marginTop: '16px',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: '#00d4aa',
                    color: '#000',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    animation: 'pulse-glow-green 2s ease-in-out infinite',
                    '@keyframes pulse-glow-green': {
                      '0%, 100%': {
                        boxShadow: '0 0 5px rgba(0, 212, 170, 0.4), 0 0 10px rgba(0, 212, 170, 0.3), 0 0 15px rgba(0, 212, 170, 0.2)',
                        transform: 'scale(1)',
                      },
                      '50%': {
                        boxShadow: '0 0 10px rgba(0, 212, 170, 0.6), 0 0 20px rgba(0, 212, 170, 0.4), 0 0 30px rgba(0, 212, 170, 0.3)',
                        transform: 'scale(1.02)',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#00c299',
                      transform: 'translateY(-1px) scale(1.02)',
                      boxShadow: '0 4px 12px rgba(0, 212, 170, 0.3), 0 0 15px rgba(0, 212, 170, 0.5)',
                      animation: 'none',
                    },
                  }}
                  onClick={handleReceiveClick}
                >
                  Receive
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: '#f6465d',
                    color: '#fff',
                    padding: '12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Sell
                </Box>
              </Box>

              {/* Quick Stats */}
              <Box sx={{ marginTop: '16px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '6px' }}
                >
                  Quick Stats
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#666', fontSize: '10px' }}
                    >
                      24h Volume
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#fff',
                        fontSize: '11px',
                        fontFamily: 'Monaco, monospace',
                      }}
                    >
                      $1.2B
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#666', fontSize: '10px' }}
                    >
                      Market Cap
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#fff',
                        fontSize: '11px',
                        fontFamily: 'Monaco, monospace',
                      }}
                    >
                      $847B
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <>
              {/* Receive UI Header */}
              <Box sx={{ borderBottom: '1px solid #1e2832', paddingBottom: '12px' }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#00d4aa',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    fontFamily: 'Arial, sans-serif',
                  }}
                >
                  Receive Crypto
                </Typography>
              </Box>

              {/* Deposit Address */}
              <Box sx={{ marginTop: '16px' }}>
                <Typography
                  variant="body2"
                  sx={{ color: '#888', marginBottom: '6px' }}
                >
                  Deposit Address
                </Typography>
                <Box
                  sx={{
                    backgroundColor: '#1e2832',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid #2a3441',
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#fff',
                      fontFamily: 'Monaco, monospace',
                      fontSize: '12px',
                      wordBreak: 'break-all',
                      paddingRight: '40px', // Space for copy button
                    }}
                  >
                    {depositAddress}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(depositAddress)}
                    sx={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#888',
                      '&:hover': {
                        color: '#00d4aa',
                      },
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* QR Code and Hashatar Side by Side */}
              <Box sx={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'center' }}>
                {/* QR Code */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#888', marginBottom: '6px' }}
                  >
                    QR Code
                  </Typography>
                  <Box
                    sx={{
                      width: { xs: '100px', md: '116px' },
                      height: { xs: '100px', md: '116px' },
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      padding: '8px',
                    }}
                  >
                    <QRCodeSVG
                      value={depositAddress}
                      size={84}
                      level="M"
                      includeMargin={false}
                    />
                  </Box>
                </Box>

                {/* Hashatar */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{ color: '#888', marginBottom: '6px' }}
                  >
                    Hashatar
                  </Typography>
                  <Box
                    sx={{
                      width: { xs: '100px', md: '116px' },
                      height: { xs: '100px', md: '116px' },
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      backgroundColor: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    ref={exchangeRef}
                  >
                    <AnimatedAvatar 
                      text={depositAddress} 
                      size={32}
                      containerRef={exchangeRef}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Instructions */}
              <Box sx={{ marginTop: '12px' }}>
                <Typography
                  variant="body2"
                  sx={{ 
                    color: '#888', 
                    fontSize: '10px', 
                    textAlign: 'center',
                    lineHeight: 1.3,
                  }}
                >
                  Send only Bitcoin to this address. Other cryptocurrencies will be lost permanently.
                </Typography>
              </Box>

              {/* Action Button */}
              <Box sx={{ marginTop: 'auto' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseReceive}
                  sx={{
                    color: '#00d4aa',
                    borderColor: '#00d4aa',
                    '&:hover': {
                      borderColor: '#00c299',
                      backgroundColor: 'rgba(0, 212, 170, 0.1)',
                    },
                  }}
                >
                  Close
                </Button>
              </Box>
            </>
          )}
          </Box>

          {/* Exchange Instruction Label */}
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: '#00d4aa',
              fontSize: '14px',
              opacity: showExchangeReceive ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
              height: '20px', // Maintain consistent height
            }}
          >
            Click "Receive" to get your deposit address
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}
